import type { CoachOffersApiGateway } from "@/modules/coachOffers/gateway/coachOffersApi.gateway"
import {
  CoachOfferDetailsDtoSchema,
  CoachOfferPriceDtoSchema,
  type CoachOfferCreationInput,
  type CoachOfferDetailsDto,
  type CoachOfferPriceDto,
  type CoachOfferUpdateInput,
  type PaymentOfferCreationInput,
} from "@/modules/coachOffers/gateway/coachOffersDto"
import { ApiClient } from "@modules/core/api/client/apiClient"
import { UserProgramDetailsDtoSchema, type UserProgramDetailsDto } from "@modules/programs/gateway/programDto"
import { type CreateUserProgramInputDto } from "@modules/programs/gateway/programInputDto"

export class CoachOffersApi implements CoachOffersApiGateway {
  private baseUrl = "/pro/offers"

  constructor(private api: ApiClient) {}

  async getCoachOffers(): Promise<CoachOfferDetailsDto[]> {
    const result = await this.api.get<CoachOfferDetailsDto[]>(this.baseUrl)
    return result.data.map((dto) => CoachOfferDetailsDtoSchema.parse(dto))
  }

  async getCoachOffer(id: string): Promise<CoachOfferDetailsDto> {
    const result = await this.api.get<CoachOfferDetailsDto>(`${this.baseUrl}/${id}`)
    return CoachOfferDetailsDtoSchema.parse(result.data)
  }

  async createCoachOffer(input: CoachOfferCreationInput): Promise<CoachOfferDetailsDto> {
    const result = await this.api.post<CoachOfferDetailsDto>(this.baseUrl, input)
    return CoachOfferDetailsDtoSchema.parse(result.data)
  }

  async updateCoachOffer(id: string, input: CoachOfferUpdateInput): Promise<CoachOfferDetailsDto> {
    const result = await this.api.put<CoachOfferDetailsDto>(`${this.baseUrl}/${id}`, input)
    return CoachOfferDetailsDtoSchema.parse(result.data)
  }

  async deleteCoachOffer(id: string): Promise<void> {
    await this.api.delete(`${this.baseUrl}/${id}`)
  }

  async createPaymentOffer(offerId: string, input: PaymentOfferCreationInput): Promise<CoachOfferPriceDto> {
    const result = await this.api.post<CoachOfferPriceDto>(`${this.baseUrl}/${offerId}/payment-offer`, input)
    return CoachOfferPriceDtoSchema.parse(result.data)
  }

  async updateCoachOfferPaymentOffer(
    offerId: string,
    paymentOfferId: string,
    input: PaymentOfferCreationInput,
  ): Promise<CoachOfferPriceDto> {
    const result = await this.api.put<CoachOfferPriceDto>(
      `${this.baseUrl}/${offerId}/payment-offer/${paymentOfferId}`,
      input,
    )
    return CoachOfferPriceDtoSchema.parse(result.data)
  }

  async deleteCoachOfferPaymentOffer(offerId: string, paymentOfferId: string): Promise<void> {
    await this.api.delete(`${this.baseUrl}/${offerId}/payment-offer/${paymentOfferId}`)
  }

  async arrangeOffersDisplayOrder(orderMap: Record<string, number>): Promise<void> {
    await this.api.put(`${this.baseUrl}/arrangeOffersDisplayOrder`, { offerOrder: orderMap })
  }

  async createCoachProgram(input: CreateUserProgramInputDto): Promise<UserProgramDetailsDto> {
    const result = await this.api.post<UserProgramDetailsDto>("/pro/programs", input)
    return UserProgramDetailsDtoSchema.parse(result.data)
  }
}
